import { useEffect, useState } from 'react';

const MS_PER_MINUTE = 60_000;

const floorToMinute = (ms: number) => ms - (ms % MS_PER_MINUTE);

/**
 * "Now", floored to the minute, re-rendering once a minute on the minute.
 *
 * A number rather than a `Date` so it can sit in a memo's dependency list and a component's props
 * without an identity trap. Floored, so two hooks mounted in the same minute agree on the value —
 * the worker calendar builds its day in two places (the column and the drop handler) and both must
 * draw a running block to the same edge.
 */
export function useClockMinute(): number {
  const [nowMs, setNowMs] = useState(() => floorToMinute(Date.now()));

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    // First tick on the next minute boundary, then every minute — so the block edge moves with
    // the current-time line rather than up to 59 seconds behind it.
    const timeout = setTimeout(() => {
      setNowMs(floorToMinute(Date.now()));
      interval = setInterval(() => setNowMs(floorToMinute(Date.now())), MS_PER_MINUTE);
    }, MS_PER_MINUTE - (Date.now() % MS_PER_MINUTE));

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  return nowMs;
}
